import { useEffect } from 'react';
import CommonHeader from '@/components/feature/CommonHeader';
import WhiteningHero from './components/WhiteningHero';
import WhiteningIntro from './components/WhiteningIntro';
import WhiteningTypes from './components/WhiteningTypes';
import WhiteningFaq from './components/WhiteningFaq';
import WhiteningAccess from './components/WhiteningAccess';
import WhiteningCta from './components/WhiteningCta';
import CommonFooter from '@/components/feature/CommonFooter';
import FloatingCta from '@/components/feature/FloatingCta';
import MobileBottomNav from '@/components/feature/MobileBottomNav';
import SeoHead from '@/components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const whiteningJsonLd = [
  {
    '@type': 'MedicalWebPage',
    '@id': `${siteUrl}/whitening/`,
    url: `${siteUrl}/whitening/`,
    name: '医療ホワイトニング｜moyuk SAPPORO 札幌',
    description: 'さいわいデンタルクリニックが提供する医療ホワイトニング。白さを感覚ではなく医療として設計します。オフィスホワイトニング・ホームホワイトニング・デュアルホワイトニング対応。',
    inLanguage: 'ja',
    about: {
      '@type': 'MedicalProcedure',
      name: '医療ホワイトニング',
      procedureType: 'http://schema.org/NoninvasiveProcedure',
      bodyLocation: '歯',
    },
    isPartOf: { '@id': `${siteUrl}/#website` },
  },
  {
    '@type': 'Service',
    name: '医療ホワイトニング',
    provider: {
      '@type': 'Dentist',
      name: 'moyuk SAPPORO',
      '@id': `${siteUrl}/#dentist`,
    },
    serviceType: '医療ホワイトニング',
    areaServed: {
      '@type': 'City',
      name: '札幌市',
    },
    description: 'オフィスホワイトニング・ホームホワイトニング・デュアルホワイトニングの3種類を提供。歯の状態に合わせた最適なホワイトニングプランをご提案します。',
  },
  {
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'ホワイトニングでしみることはありますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '一時的にしみることがありますが、多くは軽度で一過性です。ホワイトニングでは、施術中または施術後に一時的な知覚過敏の症状が出ることがあります。当院では施術前にお口の状態を確認し、むし歯や歯周病の有無・もともとの知覚過敏の有無などを踏まえて、しみやすさのリスクを事前にお伝えしています。不安が強い場合や、リスクが高いと判断した場合は、無理に施術を行うことはありません。',
        },
      },
      {
        '@type': 'Question',
        name: 'ホワイトニングでどれくらい白くなりますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '歯の状態によって白くなり方には個人差があります。ホワイトニングの効果は、歯の質・着色の原因・これまでの生活習慣などによって異なります。当院では、施術前に現在の歯の状態を確認し、期待できる白さの目安と限界をお伝えしています。納得いただいたうえで施術を選んでいただくことを大切にしています。',
        },
      },
      {
        '@type': 'Question',
        name: 'ホワイトニングの効果はどれくらい続きますか？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '生活習慣によって異なりますが、徐々に後戻りは起こります。ホワイトニング後の白さは、飲食物や喫煙の有無、日常のケアによって持続期間が変わります。時間の経過とともに少しずつ後戻りは起こりますが、定期的なケアやホワイトニングによって白さを維持しやすくなります。当院では、施術後の過ごし方やメンテナンスについてもご説明しています。',
        },
      },
    ],
  },
];

export default function WhiteningPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="医療ホワイトニング 札幌・返金保証あり | moyuk SAPPORO さいわいデンタル"
        description="札幌・moyuk SAPPOROの医療ホワイトニング。ホワイトエッセンス導入。初回施術で変化を実感できない場合の返金保証あり。施術前の無料カウンセリングで知覚過敏・適応外のリスクを事前確認。白さを感覚ではなく医療として設計します。"
        keywords="医療ホワイトニング 札幌,ホワイトニング 札幌,ホワイトエッセンス 札幌,歯 白くする 札幌,ホワイトニング 返金保証 札幌,moyuk SAPPORO,さいわいデンタル,ホワイトニング チカホ"
        canonical={`${siteUrl}/whitening/`}
        ogType="article"
        ogTitle="医療ホワイトニング 札幌・返金保証あり | moyuk SAPPORO さいわいデンタル"
        ogDescription="白さを感覚ではなく医療として設計する。ホワイトエッセンス導入の医療ホワイトニング。初回で変化を感じなければ返金保証。札幌・チカホ直結のmoyuk SAPPOROにあります。"
        ogSiteName="moyuk SAPPORO さいわいデンタル"
        ogLocale="ja_JP"
        ogImageWidth="1200"
        ogImageHeight="630"
        twitterCard="summary_large_image"
        twitterTitle="医療ホワイトニング 札幌・返金保証あり | moyuk SAPPORO"
        twitterDescription="白さを医療として設計する。ホワイトエッセンス導入。初回で変化を感じなければ返金保証。札幌チカホ直結。"
        jsonLd={whiteningJsonLd}
      />
      <CommonHeader />
      <main>
        <WhiteningHero />
        <WhiteningIntro />
        <WhiteningTypes />
        <WhiteningFaq />
        <WhiteningAccess />
        <WhiteningCta />
      </main>
      <CommonFooter />
      <FloatingCta href="https://reservation.stransa.co.jp/8878b04a60c23f1ce5b3268e265263cb" label="WEB予約（無料カウンセリング）" />
      <MobileBottomNav />
    </div>
  );
}
